import React, { useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  Divider,
  MenuItem,
  TextField,
  useTheme,
  alpha,
} from "@mui/material";
import { Description, Videocam } from "@mui/icons-material";
import {
  IN_REVIEW_STATUSES,
  SIGNED_STATUSES,
  type ActivityEvent,
} from "@/features/stationAdmin/api/stationActivityApi";
import { ReportStatus } from "@/shared/interfaces/ReportStatus";
import PaginationControls from "@/components/ui/PaginationControls";
import { useTablePagination } from "@/components/ui/useTablePagination";
import {
  ACTIVITY_GROUP_OPTIONS,
  groupActivityEvents,
  type ActivityGroupBy,
} from "./activityFeedView";

interface ActivityFeedProps {
  events: ActivityEvent[];
  loading?: boolean;
  /** Prose description of the activity window ("since your last login
   *  (Jul 9, 11:02 PM)") so the feed names the period explicitly. */
  windowDescription?: string;
}

/** Compact relative time ("just now", "3h ago", "2d ago"). Hours and days
 *  floor rather than round: a 38h-old event must read "1d ago", not a "2d
 *  ago" that contradicts its "Yesterday" group header. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

/** Maps a report status to a chip color, reusing the dashboard's status
 *  taxonomy (SIGNED_STATUSES / IN_REVIEW_STATUSES) so the chip palette can't
 *  drift from the KPI buckets. */
function statusColor(
  status: string | undefined,
): "default" | "success" | "warning" | "info" | "error" {
  if (!status) return "default";
  if (SIGNED_STATUSES.has(status)) return "success";
  if (IN_REVIEW_STATUSES.has(status)) return "warning";
  if (status === ReportStatus.GENERATING) return "info";
  return "default";
}

const FeedItem: React.FC<{ event: ActivityEvent }> = ({ event }) => {
  const theme = useTheme();
  const isReport = event.kind === "report";
  const color = isReport ? theme.palette.primary.main : theme.palette.info.main;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 1.25,
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: alpha(color, 0.12),
          color,
          width: 32,
          height: 32,
        }}
      >
        {isReport ? (
          <Description sx={{ fontSize: 18 }} />
        ) : (
          <Videocam sx={{ fontSize: 18 }} />
        )}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={event.title}>
          {event.title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mt: 0.25,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {isReport ? "Report" : "Evidence"} · {event.officerName}
          </Typography>
          {isReport && event.status && (
            <Chip
              label={event.status}
              size="small"
              color={statusColor(event.status)}
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
          )}
        </Box>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ whiteSpace: "nowrap", pt: 0.25 }}
      >
        {relativeTime(event.timestamp)}
      </Typography>
    </Box>
  );
};

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  events,
  loading,
  windowDescription,
}) => {
  const [groupBy, setGroupBy] = useState<ActivityGroupBy>("none");

  // Client-side pagination over the already-fetched feed (mirrors Home's
  // report list). Paginate the flat reverse-chronological list, then group
  // the current page — so page boundaries are stable across grouping modes
  // and "Showing X - Y of Z" always counts events, not groups.
  const { page, rowsPerPage, setPage, setRowsPerPage, start, end } =
    useTablePagination(events.length, { initialRowsPerPage: 10 });

  const pageEvents = useMemo(
    () => events.slice(start, end),
    [events, start, end],
  );
  const groups = useMemo(
    () => groupActivityEvents(pageEvents, groupBy),
    [pageEvents, groupBy],
  );

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, height: "100%", "&:hover": { boxShadow: 2 } }}
    >
      <CardHeader
        title={
          <Typography variant="subtitle1" fontWeight={600}>
            Recent Activity
          </Typography>
        }
        subheader={`Reports and evidence ${windowDescription ?? "in this period"}`}
        action={
          <TextField
            select
            size="small"
            label="Group by"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as ActivityGroupBy)}
            sx={{ minWidth: 110, mt: 0.5 }}
          >
            {ACTIVITY_GROUP_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Loading activity…
          </Typography>
        ) : events.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No station activity {windowDescription ?? "in this period"}.
          </Typography>
        ) : (
          <>
            <Stack spacing={0}>
              {groups.map((group) => (
                <Box key={group.key}>
                  {group.label && (
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: "block", pt: 1, lineHeight: 1.5 }}
                    >
                      {group.label}
                    </Typography>
                  )}
                  <Stack divider={<Divider flexItem />} spacing={0}>
                    {group.events.map((event) => (
                      <FeedItem key={event.id} event={event} />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
            <Box sx={{ mt: 1.5 }}>
              <PaginationControls
                currentPage={page}
                rowsPerPage={rowsPerPage}
                totalItems={events.length}
                onPageChange={setPage}
                onRowsPerPageChange={setRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                itemLabel="events"
                hideWhenEmpty
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
